import React, { useState } from 'react';
import { Seat, SeatMealPlanResult } from '../../interfaces/mealPlan';
import { OrderProduct, OrderCombination, Order } from '../../interfaces';
import { formatPrice } from '../../utils/formatPrice';
import { getMealPlanLabel } from '../../interfaces/mealPlan';
import MealPlanBadge from '../meal-plan/MealPlanBadge';
import { useAuth } from '../../AuthContext';
import { getSeatOrderDiscount, getSeatToPay, getSeatItemsTotal, getLineNetPrice } from '../../utils/splitTotals';

interface SplitBillViewProps {
    order: Order;
    seats: Seat[];
    products: OrderProduct[];
    combinations: OrderCombination[];
    seatResults: Record<string, SeatMealPlanResult>;
    onPaySeat: (seatId: string, method: 'room' | 'cash' | 'card') => void;
    onPayForOthers: (payerSeatId: string, targetSeatIds: string[], method: 'cash' | 'card') => void;
    /** Assign all currently-unassigned lines to the chosen seat. */
    onAssignUnassigned?: (seatId: string) => void;
}

const SplitBillView: React.FC<SplitBillViewProps> = ({
    order: _order, seats, products, combinations, seatResults, onPaySeat, onPayForOthers, onAssignUnassigned,
}) => {
    const { hasPerm } = useAuth();
    const can = (key: string) => !hasPerm || hasPerm(key);
    const [expandedSeat, setExpandedSeat] = useState<string | null>(null);
    const [payForOthersMode, setPayForOthersMode] = useState<string | null>(null);
    const [selectedTargetSeats, setSelectedTargetSeats] = useState<string[]>([]);

    const getSeatProducts = (seatId: string) => products.filter(p => p.seatId === seatId);
    const getSeatCombinations = (seatId: string) => combinations.filter(c => c.seatId === seatId);

    const getSeatTotal = (seatId: string): number => getSeatItemsTotal(seatId, products, combinations);

    const getSeatIncludedTotal = (seatId: string): number => {
        const result = seatResults[seatId];
        return result?.totalIncluded || 0;
    };

    const getSeatExtraTotal = (seatId: string): number => {
        const total = getSeatTotal(seatId);
        const included = getSeatIncludedTotal(seatId);
        return total - included;
    };

    const isSeatPaid = (seat: Seat): boolean => {
        // Use the discounted amount the seat actually owes, so a discounted payment still counts as paid.
        const toPay = getSeatToPay(seat, _order, seats, products, combinations, seatResults);
        // Match payment by seatId (unique per guest) to avoid false matches when guests share a reservation
        return (_order.payments || []).some(p => (p as any).seatId === seat.id && (Number(p.amount) - Number(p.change)) >= toPay);
    };

    const togglePayForOthers = (payerSeatId: string) => {
        if (payForOthersMode === payerSeatId) {
            setPayForOthersMode(null);
            setSelectedTargetSeats([]);
        } else {
            setPayForOthersMode(payerSeatId);
            setSelectedTargetSeats([]);
        }
    };

    const toggleTargetSeat = (seatId: string) => {
        setSelectedTargetSeats(prev =>
            prev.includes(seatId) ? prev.filter(id => id !== seatId) : [...prev, seatId]
        );
    };

    // Products not assigned to any seat
    const unassignedProducts = products.filter(p => !p.seatId);
    const unassignedCombinations = combinations.filter(c => !c.seatId);

    const orderTotal = seats.reduce(
        (sum, s) => sum + getSeatToPay(s, _order, seats, products, combinations, seatResults),
        0,
    );
    const totalPaid = (_order.payments || []).reduce((sum: number, p: any) => sum + (Number(p.amount) || 0) - (Number(p.change) || 0), 0);
    const balanceDue = Math.max(0, orderTotal - totalPaid);

    return (
        <div style={{ overflowY: 'auto', maxHeight: '60vh' }}>
            {/* Payment Summary */}
            <div style={{ padding: '10px 14px', marginBottom: 8, backgroundColor: '#F0F4FF', borderRadius: 8, border: '1px solid #D1DEFF' }}>
                <div className='d-flex justify-content-between' style={{ fontSize: 13 }}>
                    <span style={{ color: '#475467' }}>Order Total</span>
                    <span style={{ fontWeight: 600 }}>{formatPrice(orderTotal)}</span>
                </div>
                {totalPaid > 0 && (
                    <div className='d-flex justify-content-between' style={{ fontSize: 13, color: '#027A48' }}>
                        <span>Amount Paid</span>
                        <span style={{ fontWeight: 600 }}>{formatPrice(totalPaid)}</span>
                    </div>
                )}
                <div className='d-flex justify-content-between' style={{ fontSize: 14, fontWeight: 700, color: balanceDue > 0 ? '#B93815' : '#027A48', borderTop: '1px solid #D1DEFF', paddingTop: 4, marginTop: 4 }}>
                    <span>Balance Due</span>
                    <span>{formatPrice(balanceDue)}</span>
                </div>
            </div>

            {seats.map((seat) => {
                const seatProducts = getSeatProducts(seat.id);
                const seatCombinations = getSeatCombinations(seat.id);
                const included = getSeatIncludedTotal(seat.id);
                const extra = getSeatExtraTotal(seat.id);
                const seatDiscount = getSeatOrderDiscount(seat, _order, seats, products, combinations, seatResults);
                const toPay = getSeatToPay(seat, _order, seats, products, combinations, seatResults);
                const isExpanded = expandedSeat === seat.id;
                const result = seatResults[seat.id];
                const isHotelGuest = seat.type === 'hotel_guest';

                const seatPaid = isSeatPaid(seat);

                return (
                    <div key={seat.id} style={{
                        border: seatPaid ? '1px solid #A6F4C5' : '1px solid #EAECF0', borderRadius: 8, marginBottom: 8,
                        overflow: 'hidden', opacity: seatPaid ? 0.75 : 1,
                    }}>
                        {/* Seat header */}
                        <div
                            onClick={() => setExpandedSeat(isExpanded ? null : seat.id)}
                            style={{
                                padding: '10px 14px', cursor: 'pointer',
                                backgroundColor: seatPaid ? '#ECFDF3' : (isHotelGuest ? '#F0F4FF' : '#F9FAFB'),
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            }}
                        >
                            <div className='d-flex align-items-center gap-2'>
                                <span style={{
                                    width: 24, height: 24, borderRadius: '50%', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                                    backgroundColor: seatPaid ? '#027A48' : (isHotelGuest ? '#1643E0' : '#475467'), color: '#fff',
                                }}>
                                    {seatPaid ? '\u2713' : seat.seatNumber}
                                </span>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 13, color: seatPaid ? '#027A48' : '#344054' }}>
                                        {seat.guestName || `Seat ${seat.seatNumber}`}
                                        {isHotelGuest && seat.roomNumber && (
                                            <span style={{ color: '#667085', fontWeight: 400 }}> (Room {seat.roomNumber})</span>
                                        )}
                                        {seatPaid && (
                                            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, backgroundColor: '#D1FADF', color: '#027A48' }}>Paid</span>
                                        )}
                                    </div>
                                    {isHotelGuest && seat.rateName && (
                                        <span style={{ fontSize: 11, color: '#667085' }}>
                                            {getMealPlanLabel(seat.rateName)}
                                            {result?.currentMealPeriod && ` - ${result.currentMealPeriod}`}
                                        </span>
                                    )}
                                    {!isHotelGuest && (
                                        <span style={{ fontSize: 11, color: '#667085' }}>Walk-in Guest</span>
                                    )}
                                </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: seatPaid ? '#027A48' : (toPay > 0 ? '#B93815' : '#027A48') }}>
                                    {seatPaid ? 'Paid' : formatPrice(toPay)}
                                </div>
                                {isHotelGuest && included > 0 && (
                                    <div style={{ fontSize: 10, color: '#027A48' }}>
                                        {formatPrice(included)} included
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Expanded items */}
                        {isExpanded && (
                            <div style={{ padding: '0 14px 10px' }}>
                                {(() => {
                                    // Group identical-looking rows together for display only.
                                    interface SbGroup { rep: typeof seatProducts[number]; qty: number; status: 'included' | 'extra' | 'none'; }
                                    const groups: SbGroup[] = [];
                                    seatProducts.forEach((p) => {
                                        const isWaived = !!(p as any).mealPlanOverride?.waived;
                                        const rawStatus = ((p as any).mealPlanStatus as 'included' | 'extra' | 'none') || 'none';
                                        const visualStatus: 'included' | 'extra' | 'none' = isWaived ? 'included' : rawStatus;
                                        const matchIdx = groups.findIndex(g => {
                                            const r = g.rep;
                                            if (r.productId !== p.productId) return false;
                                            if ((r.note || '') !== (p.note || '')) return false;
                                            if ((r.addons?.length || 0) > 0 || (p.addons?.length || 0) > 0) return false;
                                            if ((r.variants?.length || 0) > 0 || (p.variants?.length || 0) > 0) return false;
                                            if ((r.discount || 0) !== (p.discount || 0)) return false;
                                            if ((r.discountType || '') !== (p.discountType || '')) return false;
                                            if (g.status !== visualStatus) return false;
                                            return true;
                                        });
                                        if (matchIdx >= 0) {
                                            groups[matchIdx].qty += p.qty || 1;
                                        } else {
                                            groups.push({ rep: p, qty: p.qty || 1, status: visualStatus });
                                        }
                                    });
                                    return groups.map((group, gIdx) => {
                                        const prod = group.rep;
                                        return (
                                            <div key={`sb-group-${gIdx}`} className='d-flex justify-content-between align-items-center'
                                                style={{ padding: '4px 0', borderBottom: '1px solid #F2F4F7', fontSize: 12 }}
                                            >
                                                <div className='d-flex align-items-center gap-2'>
                                                    <span style={{ color: '#344054' }}>{group.qty}x {prod.name}</span>
                                                    {group.status !== 'none' && (
                                                        <MealPlanBadge status={group.status} small />
                                                    )}
                                                </div>
                                                <span style={{ fontWeight: 500, color: group.status === 'included' ? '#027A48' : '#344054' }}>
                                                    {formatPrice(group.status === 'included' ? 0 : getLineNetPrice(prod.price, prod.discount, prod.discountType) * group.qty)}
                                                </span>
                                            </div>
                                        );
                                    });
                                })()}
                                {seatCombinations.map((comb) => (
                                    <div key={comb.id} className='d-flex justify-content-between align-items-center'
                                        style={{ padding: '4px 0', borderBottom: '1px solid #F2F4F7', fontSize: 12 }}
                                    >
                                        <div className='d-flex align-items-center gap-2'>
                                            <span style={{ color: '#344054' }}>{comb.combinationName}</span>
                                            {comb.mealPlanStatus && comb.mealPlanStatus !== 'none' && (
                                                <MealPlanBadge status={comb.mealPlanStatus} small />
                                            )}
                                        </div>
                                        <span style={{ fontWeight: 500, color: '#344054' }}>
                                            {formatPrice(comb.price)}
                                        </span>
                                    </div>
                                ))}

                                {/* Summary line */}
                                <div style={{ borderTop: '1px solid #EAECF0', marginTop: 6, paddingTop: 6 }}>
                                    {isHotelGuest && included > 0 && (
                                        <div className='d-flex justify-content-between' style={{ fontSize: 11, color: '#027A48' }}>
                                            <span>Included in plan</span>
                                            <span>{formatPrice(included)}</span>
                                        </div>
                                    )}
                                    {extra > 0 && isHotelGuest && (
                                        <div className='d-flex justify-content-between' style={{ fontSize: 11, color: '#B93815' }}>
                                            <span>Extra charges</span>
                                            <span>{formatPrice(extra)}</span>
                                        </div>
                                    )}
                                    {seatDiscount > 0 && (
                                        <div className='d-flex justify-content-between' style={{ fontSize: 11, color: '#B93815' }}>
                                            <span>Discount</span>
                                            <span>-{formatPrice(seatDiscount)}</span>
                                        </div>
                                    )}
                                    <div className='d-flex justify-content-between' style={{ fontSize: 13, fontWeight: 700, color: '#344054', marginTop: 2 }}>
                                        <span>To pay</span>
                                        <span>{formatPrice(toPay)}</span>
                                    </div>
                                </div>

                                {/* Payment buttons */}
                                {!seatPaid && (toPay > 0 || (isHotelGuest && included > 0)) && (
                                    <div className='d-flex gap-2 mt-2' style={{ flexWrap: 'wrap' }}>
                                        {isHotelGuest && can('pos-app.payment.room-charge') && (
                                            <button
                                                onClick={() => onPaySeat(seat.id, 'room')}
                                                style={{
                                                    padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                                    border: '1px solid #1643E0', backgroundColor: '#EEF4FF',
                                                    color: '#1643E0', cursor: 'pointer',
                                                }}
                                            >
                                                {toPay > 0 ? `Charge to Room ${seat.roomNumber}` : `Record Meal Plan (Room ${seat.roomNumber})`}
                                            </button>
                                        )}
                                        {toPay > 0 && (
                                            <>
                                                {can('pos-app.payment.cash') && (
                                                <button
                                                    onClick={() => onPaySeat(seat.id, 'cash')}
                                                    style={{
                                                        padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                                        border: '1px solid #027A48', backgroundColor: '#ECFDF3',
                                                        color: '#027A48', cursor: 'pointer',
                                                    }}
                                                >
                                                    Cash
                                                </button>
                                                )}
                                                {can('pos-app.payment.card') && (
                                                <button
                                                    onClick={() => onPaySeat(seat.id, 'card')}
                                                    style={{
                                                        padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                                        border: '1px solid #6941C6', backgroundColor: '#F9F5FF',
                                                        color: '#6941C6', cursor: 'pointer',
                                                    }}
                                                >
                                                    Card
                                                </button>
                                                )}
                                                <button
                                                    onClick={() => togglePayForOthers(seat.id)}
                                                    style={{
                                                        padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 500,
                                                        border: '1px solid #D0D5DD', backgroundColor: payForOthersMode === seat.id ? '#F2F4F7' : '#fff',
                                                        color: '#475467', cursor: 'pointer',
                                                    }}
                                                >
                                                    {payForOthersMode === seat.id ? 'Cancel' : 'Pay for others'}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* Pay for others selection */}
                                {payForOthersMode === seat.id && (
                                    <div style={{
                                        marginTop: 8, padding: 10, backgroundColor: '#F9FAFB',
                                        borderRadius: 6, border: '1px solid #EAECF0',
                                    }}>
                                        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, color: '#344054' }}>
                                            Select seats to pay for:
                                        </div>
                                        {seats.filter(s => s.id !== seat.id && !isSeatPaid(s)).map(targetSeat => (
                                            <label key={targetSeat.id}
                                                className='d-flex align-items-center gap-2'
                                                style={{ fontSize: 12, cursor: 'pointer', padding: '3px 0' }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTargetSeats.includes(targetSeat.id)}
                                                    onChange={() => toggleTargetSeat(targetSeat.id)}
                                                />
                                                Seat {targetSeat.seatNumber}: {targetSeat.guestName || 'Guest'} ({formatPrice(getSeatToPay(targetSeat, _order, seats, products, combinations, seatResults))})
                                            </label>
                                        ))}
                                        {selectedTargetSeats.length > 0 && (
                                            <div className='d-flex gap-2 mt-2'>
                                                {can('pos-app.payment.cash') && (
                                                <button
                                                    onClick={() => {
                                                        onPayForOthers(seat.id, selectedTargetSeats, 'cash');
                                                        setPayForOthersMode(null);
                                                        setSelectedTargetSeats([]);
                                                    }}
                                                    style={{
                                                        padding: '6px 14px', borderRadius: 6,
                                                        fontSize: 12, fontWeight: 600, border: '1px solid #027A48',
                                                        backgroundColor: '#ECFDF3', color: '#027A48', cursor: 'pointer',
                                                    }}
                                                >
                                                    Pay Cash
                                                </button>
                                                )}
                                                {can('pos-app.payment.card') && (
                                                <button
                                                    onClick={() => {
                                                        onPayForOthers(seat.id, selectedTargetSeats, 'card');
                                                        setPayForOthersMode(null);
                                                        setSelectedTargetSeats([]);
                                                    }}
                                                    style={{
                                                        padding: '6px 14px', borderRadius: 6,
                                                        fontSize: 12, fontWeight: 600, border: '1px solid #6941C6',
                                                        backgroundColor: '#F9F5FF', color: '#6941C6', cursor: 'pointer',
                                                    }}
                                                >
                                                    Pay Card
                                                </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Unassigned items */}
            {(unassignedProducts.length > 0 || unassignedCombinations.length > 0) && (
                <div style={{
                    border: '1px solid #FEC84B', borderRadius: 8, marginBottom: 8,
                    padding: '10px 14px', backgroundColor: '#FFFCF5',
                }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#B54708', marginBottom: 6 }}>
                        Unassigned Items
                    </div>
                    {unassignedProducts.map(prod => (
                        <div key={prod.id} className='d-flex justify-content-between' style={{ fontSize: 12, padding: '2px 0' }}>
                            <span>{prod.qty}x {prod.name}</span>
                            <span>{formatPrice(getLineNetPrice(prod.price, prod.discount, prod.discountType) * prod.qty)}</span>
                        </div>
                    ))}
                    {unassignedCombinations.map(comb => (
                        <div key={comb.id} className='d-flex justify-content-between' style={{ fontSize: 12, padding: '2px 0' }}>
                            <span>{comb.combinationName}</span>
                            <span>{formatPrice(getLineNetPrice(comb.price, comb.discount, comb.discountType))}</span>
                        </div>
                    ))}
                    {onAssignUnassigned && seats.length > 0 && (
                        <div style={{
                            marginTop: 10, paddingTop: 8, borderTop: '1px dashed #FEC84B',
                            display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
                        }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#B54708' }}>Assign all to:</span>
                            {seats.map(s => {
                                const isHotel = s.type === 'hotel_guest';
                                const label = (s.guestName || `Guest ${s.seatNumber}`).split(' ')[0];
                                return (
                                    <button
                                        key={s.id}
                                        onClick={() => onAssignUnassigned(s.id)}
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 5,
                                            padding: '4px 10px', borderRadius: 14, fontSize: 12, fontWeight: 600,
                                            border: `1px solid ${isHotel ? '#1643E0' : '#D0D5DD'}`,
                                            backgroundColor: isHotel ? '#EEF4FF' : '#fff',
                                            color: isHotel ? '#1643E0' : '#344054', cursor: 'pointer',
                                        }}
                                    >
                                        <span style={{
                                            width: 16, height: 16, borderRadius: '50%', display: 'inline-flex',
                                            alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700,
                                            backgroundColor: isHotel ? '#1643E0' : '#E4E7EC',
                                            color: isHotel ? '#fff' : '#475467',
                                        }}>{s.seatNumber}</span>
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SplitBillView;
